/**
 * Role idle lock (RL10) — after inactivity, clear the PIN role and reopen CodeEntry.
 *
 * Deliberately NOT a full business logout: currentUser/currentBusiness stay so the
 * operator only re-enters the PIN. Cash-register pause remains a separate, explicit flow.
 *
 * KV override: `roleIdleTimeoutMs` — number of ms, or 0 to disable.
 */

import { kvGet } from '../infrastructure/storage/kvAccess';

export const ROLE_IDLE_TIMEOUT_KV_KEY = 'roleIdleTimeoutMs';

/** 15 minutes — shared POS / manager desk default. */
export const DEFAULT_ROLE_IDLE_TIMEOUT_MS = 15 * 60 * 1000;

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'mousemove', 'scroll'];

/** High-frequency events — throttle re-arm so every pixel doesn't clearTimeout. */
const THROTTLED_ACTIVITY_EVENTS = new Set(['mousemove', 'scroll']);
const ACTIVITY_THROTTLE_MS = 1000;

/**
 * @returns {number} ms until lock; 0 = disabled
 */
export function getRoleIdleTimeoutMs() {
  try {
    const raw = kvGet(ROLE_IDLE_TIMEOUT_KV_KEY);
    if (raw == null || raw === '') return DEFAULT_ROLE_IDLE_TIMEOUT_MS;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return DEFAULT_ROLE_IDLE_TIMEOUT_MS;
    return Math.floor(n);
  } catch (_) {
    return DEFAULT_ROLE_IDLE_TIMEOUT_MS;
  }
}

/**
 * @param {{
 *   timeoutMs?: number,
 *   onIdle: () => void,
 *   isPaused?: () => boolean
 * }} options
 *   isPaused: when true, the idle clock does not run (CodeEntry / cash modal / logout).
 * @returns {{ dispose: () => void, bump: () => void }}
 */
export function startRoleIdleWatcher({
  timeoutMs = getRoleIdleTimeoutMs(),
  onIdle,
  isPaused = () => false
} = {}) {
  if (typeof window === 'undefined' || !timeoutMs || timeoutMs <= 0) {
    return { dispose() {}, bump() {} };
  }
  if (typeof onIdle !== 'function') {
    return { dispose() {}, bump() {} };
  }

  let timer = null;
  let disposed = false;
  let lastBumpAt = 0;

  const clear = () => {
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const arm = () => {
    clear();
    if (disposed || isPaused()) return;
    timer = setTimeout(() => {
      timer = null;
      if (disposed || isPaused()) return;
      try {
        onIdle();
      } catch (err) {
        console.warn('[roleIdleLock] onIdle failed:', err?.message || err);
      }
    }, timeoutMs);
  };

  const bump = (evt) => {
    if (disposed || isPaused()) return;
    const type = evt?.type;
    if (type && THROTTLED_ACTIVITY_EVENTS.has(type)) {
      const now = Date.now();
      if (now - lastBumpAt < ACTIVITY_THROTTLE_MS) return;
      lastBumpAt = now;
    } else {
      lastBumpAt = Date.now();
    }
    arm();
  };

  const onVisibility = () => {
    if (document.visibilityState === 'visible') bump();
  };

  ACTIVITY_EVENTS.forEach((evt) => {
    window.addEventListener(evt, bump, { passive: true, capture: true });
  });
  document.addEventListener('visibilitychange', onVisibility);

  arm();

  return {
    bump,
    dispose() {
      disposed = true;
      clear();
      ACTIVITY_EVENTS.forEach((evt) => {
        window.removeEventListener(evt, bump, { capture: true });
      });
      document.removeEventListener('visibilitychange', onVisibility);
    }
  };
}
